// SourceRef — frontend shape + helpers
//
// Mirrors backend/domain/value_objects/source_ref.py. Any surface that
// receives a SourceRef-shaped object (chat citations today; checklist
// match evidence, contradiction surfaces, timeline events tomorrow)
// uses these helpers to render it as a clickable link into the
// document viewer.
//
// The canonical shape (what the backend serializes via to_dict()):
//
//   {
//     document_id: string,
//     filename: string,
//     page?: number,             // 1-based, paged docs
//     timestamp?: number,        // seconds, media
//     snippet: string,           // verbatim source text for highlight
//     kind: string,              // "quote" | "search" | "fact" | ...
//     verified: boolean,
//     ref?: number,              // sideband [N] index
//     confidence?: number,       // 0..1
//     cited_char_range?: [number, number],
//                                // chunk-relative [start, end) offsets of
//                                // the exact cited span (the quote / the
//                                // person's name) WITHIN the chunk text.
//                                // Producer-agnostic: Arc's grounded
//                                // verifier and the witnesses name-locator
//                                // both emit chunk-relative offsets. The
//                                // viewer composes these onto the page to
//                                // highlight the span, not the whole chunk.
//   }
//
// Source kinds — mirror the backend constants. Add new values here when
// the backend adds a producer.
export const SOURCE_KIND = Object.freeze({
  QUOTE: 'quote',
  SEARCH: 'search',
  FACT: 'fact',
  CONTRADICTION: 'contradiction',
  READ_DOC: 'read_doc',
  MATCH: 'match',
  LIST: 'list',
  NONE: 'none',
});

// EvidenceLocation kinds — mirror the backend value object. The viewer
// dispatches on these to pick the right rendering strategy:
//
//   char_range  — slice the doc's canonical page text at [start, end),
//                 then locate that exact substring in the rendered DOM
//                 and wrap it. No fuzzy matching.
//   time_range  — seek the media player; outline the transcript segment
//                 whose [start, end) covers the cited range.
//   bbox        — draw a transparent rectangle overlay at the bbox on
//                 the PDF canvas. Pixel-precise, immune to text-layer
//                 issues. (Future enrichment.)
//   none        — no stored location. Viewer falls back to fuzzy
//                 snippet matching (legacy chunks pre-SOTA-Phase B).
export const LOCATION_KIND = Object.freeze({
  CHAR_RANGE: 'char_range',
  TIME_RANGE: 'time_range',
  BBOX: 'bbox',
  NONE: 'none',
});

/**
 * Read the typed location from a SourceRef-shaped object and return a
 * normalized {kind, ...} object suitable for dispatch.
 *
 * Always returns a value — defaults to `{kind: 'none'}` if the ref has
 * no `location` (legacy chunks). Callers can do a plain switch on the
 * `kind` field.
 */
export function getLocation(ref) {
  const loc = ref?.location;
  if (!loc || !loc.kind) return { kind: LOCATION_KIND.NONE };
  return loc;
}

// Cap for `q=` in the URL. The backend's SourceRef caps snippet at
// 2000 chars (the typical chunk size). Match that here so a full
// indexed chunk text always fits in the URL — that's the SOTA path
// where the viewer's match is against verbatim indexed text, not a
// preview or model paraphrase. Browsers handle hash-route URLs at 2 KB
// without trouble.
const URL_SNIPPET_CAP = 2000;

/**
 * Build a viewer deep-link URL from a SourceRef.
 *
 * The viewer's DocumentDeepLink component consumes ?doc, ?page, ?t,
 * ?q, and ?cr from the hash route. We standardize on this contract so
 * any upstream producer (citation, match evidence, contradiction view,
 * etc.) builds links the same way.
 *
 *   ?cr=<start>-<end>  — chunk-relative char offsets of the exact cited
 *                        span. Optional; absence = whole-chunk highlight
 *                        (today's behavior). The viewer composes this
 *                        onto the chunk's page anchor to slice a small
 *                        needle (the cited sentence / name) instead of
 *                        the whole paragraph.
 *
 * Help-mode special case: if the ref carries ``help_slug``, the link
 * points at the dedicated help-article page (``/help/<slug>``) instead
 * of the document viewer modal. ``help_anchor`` scrolls to the cited
 * section; ``q`` is still used for the in-article snippet highlight.
 *
 * @param {object} ref       SourceRef-shaped object
 * @param {string} pathname  Current SPA pathname (so back-nav returns here)
 * @returns {string|null}    Hash-route URL, or null if no document_id / help_slug
 */
export function buildEvidenceUrl(ref, pathname) {
  if (!ref) return null;
  const snip = (ref.snippet || '').trim();

  // Help-mode link — purpose-built /help/<slug> page (anchor + highlight).
  // BrowserRouter URLs (no leading `#`); pre-2026-05-24 SPA used HashRouter.
  if (ref.help_slug) {
    const params = new URLSearchParams();
    if (ref.help_anchor) params.set('anchor', ref.help_anchor);
    if (snip) params.set('q', snip.slice(0, URL_SNIPPET_CAP));
    const qs = params.toString();
    return `/help/${ref.help_slug}${qs ? `?${qs}` : ''}`;
  }

  if (!ref.document_id) return null;
  const params = new URLSearchParams();
  params.set('doc', ref.document_id);
  // chunk = the SOTA path. When present, the viewer fetches the chunk
  // by ID and dispatches on its EvidenceLocation; the `q` param then
  // serves only as a fallback for legacy chunks without a stored
  // location.
  if (ref.chunk_id) params.set('chunk', ref.chunk_id);
  if (ref.timestamp != null) params.set('t', String(ref.timestamp));
  else if (Number.isFinite(ref.anchor?.time_start)) params.set('t', String(ref.anchor.time_start));
  // page=0 is an "unknown page" sentinel from some extractors — writing
  // it to the URL silently pinned citations to page 1. Skip it.
  if (typeof ref.page === 'number' && ref.page > 0) params.set('page', String(ref.page));
  if (snip) params.set('q', snip.slice(0, URL_SNIPPET_CAP));
  // cr = chunk-relative [start, end) of the exact cited span. Two ints,
  // negligible URL cost. The viewer composes it onto the chunk's page
  // anchor to highlight the cited sentence/name, not the whole chunk.
  // Optional — only emitted when the producer pinpointed a sub-range.
  const cr = ref.cited_char_range;
  if (Array.isArray(cr) && cr.length === 2
      && Number.isFinite(cr[0]) && Number.isFinite(cr[1]) && cr[1] > cr[0]) {
    params.set('cr', `${cr[0]}-${cr[1]}`);
  }
  // a = the server-resolved anchor ("resolve once, render rects").
  // Compact encoding: pin:<page>:<start>-<end> | page:<page> | doc.
  // When present the viewer scrolls + highlights from these verified
  // page-text offsets directly — no chunk fetch, no DOM text search.
  // Rects derive deterministically from the page's extraction blocks.
  const a = encodeAnchorParam(ref.anchor);
  if (a) params.set('a', a);
  return `${pathname}?${params.toString()}`;
}

/**
 * Encode a backend anchor object into the compact `a=` URL param.
 * Returns null when the anchor adds nothing over the other params
 * (time anchors ride on `t`; malformed anchors are dropped).
 */
export function encodeAnchorParam(anchor) {
  if (!anchor || typeof anchor !== 'object') return null;
  const { status } = anchor;
  if (status === 'pinpoint'
      && Number.isFinite(anchor.char_start) && Number.isFinite(anchor.char_end)
      && anchor.char_end > anchor.char_start) {
    const page = Number.isFinite(anchor.page) && anchor.page > 0 ? anchor.page : 0;
    return `pin:${page}:${anchor.char_start}-${anchor.char_end}`;
  }
  if (status === 'page' && Number.isFinite(anchor.page) && anchor.page > 0) {
    return `page:${anchor.page}`;
  }
  if (status === 'document') return 'doc';
  return null;
}

/**
 * Decode the `a=` URL param back into an anchor object ({status, page,
 * char_start, char_end}). Malformed input → null (viewer falls back to
 * the chunk / q path — never worse than before anchors existed).
 */
export function decodeAnchorParam(raw) {
  if (!raw) return null;
  if (raw === 'doc') return { status: 'document' };
  let m = raw.match(/^page:(\d+)$/);
  if (m) return { status: 'page', page: parseInt(m[1], 10) };
  m = raw.match(/^pin:(\d+):(\d+)-(\d+)$/);
  if (m) {
    const page = parseInt(m[1], 10);
    const start = parseInt(m[2], 10);
    const end = parseInt(m[3], 10);
    if (end <= start) return null;
    return {
      status: 'pinpoint',
      page: page > 0 ? page : null,
      char_start: start,
      char_end: end,
    };
  }
  return null;
}

/**
 * Build a human-readable label for a SourceRef.
 *
 * Examples:
 *   "report.pdf, p.5"
 *   "interview.wav @ 12:34"
 *   "case_notes.txt"
 *   "Source 3"   (when filename is missing)
 *
 * @param {object} ref
 * @returns {string}
 */
export function buildEvidenceLabel(ref) {
  if (!ref) return '';
  // Help-mode label uses the article filename (which is "<Title>.md")
  // stripped of the extension, plus the section anchor as a hint.
  if (ref.help_slug) {
    const baseName = (ref.filename || ref.help_slug).replace(/\.md$/i, '');
    if (ref.help_anchor && ref.help_anchor !== 'intro') {
      const heading = ref.help_anchor.replace(/-/g, ' ');
      return `${baseName} › ${heading}`;
    }
    return baseName;
  }
  const name = ref.filename || (ref.ref != null ? `Source ${ref.ref}` : 'Source');
  if (ref.timestamp != null) {
    return `${name} @ ${formatTimestamp(ref.timestamp)}`;
  }
  // page=0 is a sentinel some upstream extractors emit for "unknown
  // page" (PDFs are 1-indexed everywhere else). Skip it in the label
  // so "p.0" never reaches the user. Treating non-positive as no-page.
  if (typeof ref.page === 'number' && ref.page > 0) {
    return `${name}, p.${ref.page}`;
  }
  return name;
}

function formatTimestamp(secs) {
  const total = Math.floor(secs);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// "MM:SS" or "HH:MM:SS" → seconds. Useful when a producer encodes
// timestamps as strings (e.g., chat sideband cite.timestamp).
export function timestampToSeconds(ts) {
  if (typeof ts === 'number') return ts;
  if (!ts) return null;
  const parts = String(ts).split(':').map(Number);
  if (parts.some(p => Number.isNaN(p))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || null;
}

/**
 * Predicate: should this ref render as a clickable link?
 * Help-mode refs link via help_slug; case-doc refs link via document_id.
 */
export function isLinkable(ref) {
  if (!ref) return false;
  return !!(ref.help_slug || ref.document_id);
}
